import 'regenerator-runtime/runtime';
import { UserManager, Log, User, SigninRedirectArgs } from 'oidc-client-ts';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { GetAppSettings } from '../AppSettings';
import { getQuerystringParameters } from '../helpers/utility';
import { NavigateFunction } from 'react-router-dom';

export type RouterOptions = {
  navigate: NavigateFunction;
};

let accessToken: string = null;
let idToken: string = null;
let userManager: UserManager = null;

Log.setLogger(console);

function onUserLoaded(user: User) {
  accessToken = user.access_token;
  idToken = user.id_token;
}

function onStorageChanged(e: StorageEvent) {
  if (e.key === 'logout') {
    InternalLogout();
  }
  // when Independent User clicks on switch account, all other open tabs will redirect to marketing home page.
  // This may change and is placeholder for now while customers beta test
  if (e.key === 'switchAccount') {
    window.open('https://www.goformz.com/', '_self');
  }
  if (e.key === 'accountDeleted') {
    // here we're outside the context of the router and we're navigating to
    // an account deleted page, so it's already to depend on window's location
    // directly
    window.location.replace('/accountdeleted');
  }
}

export function switchAccounts() {
  if (window.Intercom) {
    window.Intercom('shutdown');
  }

  // reset the auth:redirect url so we don't get redirected to /iuprofile after selecting an account
  sessionStorage.setItem('auth:redirect', '/');

  const signinRequestSettings: SigninRedirectArgs = {};
  signinRequestSettings.prompt = 'login';
  signinRequestSettings.extraQueryParams = { switchAccount: true };

  userManager.signinRedirect(signinRequestSettings);
}

export function LoadUserManager() {
  const appSettings = GetAppSettings();

  userManager = new UserManager({
    authority: appSettings.AuthenticationServiceUrl,
    client_id: appSettings.AuthClientId,
    client_secret: appSettings.AuthClientSecret,
    redirect_uri: window.location.origin,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    response_mode: 'query',
    scope: 'openid profile basic',
    silent_redirect_uri: `${window.location.origin}/silent_renew`,
    popup_redirect_uri: `${window.location.origin}/popup_callback`,
    loadUserInfo: true,
    filterProtocolClaims: true,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 20 // defaults to 60
  });
  userManager.events.addUserLoaded(onUserLoaded);
  window.addEventListener('storage', (e) => onStorageChanged(e));
}

export function GetAccessToken() {
  return accessToken;
}

export function GetUserManager() {
  return userManager;
}

export async function Logout() {
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
  await InternalLogout();
  localStorage.clear();
  sessionStorage.clear();
  localStorage.setItem('logout', uuidv4());
}

async function InternalLogout() {
  const user = await userManager.getUser();
  if (user) {
    await userManager.removeUser();
    userManager.signoutRedirect({ id_token_hint: idToken });
  }
}

export async function LogoutNoRedirect() {
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
  await userManager.removeUser();
  userManager.signoutSilent({ id_token_hint: idToken });
  localStorage.clear();
  sessionStorage.clear();
  localStorage.setItem('accountDeleted', uuidv4());
}

export async function HandleAuthentication(setAuthenticationError: React.Dispatch<React.SetStateAction<string>>, routerOptions: RouterOptions): Promise<void> {
  const queryParams = getQuerystringParameters(true, true);
  const hostParts = window.location.hostname.split('.');

  let idpName = null;
  if (queryParams.sso) {
    idpName = queryParams.sso;
  } else if (hostParts.length === 4) {
    // this is to support urls like att.app.goformz.com
    idpName = hostParts[0];
  }
  const signinRequestSettings: Record<string, string> = idpName ? { acr_values: `idp:${idpName}` } : {};

  if (queryParams.prompt === 'login') {
    signinRequestSettings.prompt = 'login';
  }

  if (queryParams.signup) {
    // if signup passed in, go to the signup page
    return userManager.signinRedirect({
      acr_values: 'signup',
      prompt: 'login'
    });
  } else if (queryParams.code && queryParams.state && window.location.pathname === '/') {
    // returning from login page
    try {
      await userManager.signinRedirectCallback();
      const path = sessionStorage.getItem('auth:redirect') || '/';

      routerOptions.navigate(path, { state: { authRedirect: true } });
    } catch (err) {
      console.error(err);

      if (err.message.indexOf('exp is in the past') > -1) {
        setAuthenticationError('There was an error logging you in. Please verify your system clock has the correct time and retry.');
        routerOptions.navigate('/');
      } else if (err.message.indexOf('iat is in the future') > -1) {
        setAuthenticationError('There was an error logging you in. Please verify your system clock has the correct time and retry.');
      } else if (err.message.indexOf('No matching state found in storage') > -1) {
        // we want to force prompt a login so we don't end up in an endless redirect loop
        signinRequestSettings.prompt = 'login';
        userManager.signinRedirect(signinRequestSettings);
      } else {
        setAuthenticationError('There was an error logging you in. Please retry. If this error persists, please contact support.');
      }
    }
    return;
  }

  const user = await userManager.getUser();
  if (!user) {
    // never been logged in
    sessionStorage.setItem('auth:redirect', getRedirectUrl());
    userManager.signinRedirect(signinRequestSettings);
  } else {
    // we think we're logged in so lets try to renew. If that fails we need to login
    try {
      await userManager.signinSilent();
    } catch (err) {
      sessionStorage.setItem('auth:redirect', getRedirectUrl());
      userManager.signinRedirect(signinRequestSettings);
    }
  }
}

function getRedirectUrl() {
  let url = window.location.href.replace(window.location.origin, '');
  url = url.replace(/&prompt=login$/g, '');
  return url;
}

function showReloginPrompt(callback) {
  import('../helpers/MessageUtility').then((messageUtility) => {
    messageUtility.showMessage(
      'Session Expired',
      'For security reasons, your session has expired.',
      async (didConfirm) => {
        if (!didConfirm) {
          return;
        }

        // sign in
        try {
          const currentUser = await userManager.getUser();
          await userManager.signinPopup();
          const newUser = await userManager.getUser();

          //if they logged in as a different user then boot them to the login screen.
          //we dont trust this sort of tom foolery
          if (currentUser.profile.sub !== newUser.profile.sub) {
            await InternalLogout();
          }

          callback();
        } catch (err) {
          console.error(err);
        }
      },
      'Log in'
    );
  });
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    if (error.config && (error.config.headers['X-Policy'] || error.config.headers['X-Policy-Token'])) {
      // don't retry for public forms
      return Promise.reject(error);
    }

    if (error.config && status === 401) {
      if (error.config.retryCount > 0) {
        console.log(`Not going to retry because retryCount is ${error.config.retryCount}`);
        return Promise.reject(error);
      }

      await new Promise<void>(async (callback) => {
        try {
          await userManager.signinSilent();
          callback();
        } catch (err) {
          showReloginPrompt(callback);
        }
      });

      // retry
      const retryRequest = error.config;
      retryRequest.headers.Authorization = `Bearer ${GetAccessToken()}`;
      retryRequest.retryCount = retryRequest.retryCount || 0;
      retryRequest.retryCount++;

      return axios.request(retryRequest);
    }
    return Promise.reject(error);
  }
);
